// src/pages/UserAssignmentsPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Layout from "../components/layout/Layout";
import RolesReferenceTable from "../components/admin/RolesReferenceTable";
import { useBackNavigation } from "../hooks/useBackNavigation";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import { useAuth } from "../context/AuthContext";

import {
  Box,
  Typography,
  Alert,
  Button,
  Paper,
  Checkbox,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import {
  getAllRoles,
  getUserAssignments,
  updateUserAssignments,
} from "../api/userApi";

function UserAssignmentsPage() {
  const { userId } = useParams();
  const goBack = useBackNavigation("/admin/users");
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const { user } = useAuth();

  const [properties, setProperties] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assignedProperties, setAssignedProperties] = useState([]);
  const [, setAssignedUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isGlobalRole, setIsGlobalRole] = useState(false);
  const [roleDisplayName, setRoleDisplayName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [isCurrentUser, setIsCurrentUser] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    setCustomPath([
      { label: "Settings", path: "/settings" },
      { label: "User Management", path: "/admin/users" },
      { label: "User Assignments", path: null },
    ]);
    return () => clearCustomPath();
  }, [setCustomPath, clearCustomPath]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [assignments, rolesData] = await Promise.all([
          getUserAssignments(userId),
          getAllRoles(),
        ]);

        setProperties(assignments.properties || []);
        setAssignedProperties(assignments.property_ids || []);
        setAssignedUnits(assignments.unit_ids || []);
        setIsGlobalRole(Boolean(assignments.is_global_role));
        setRoleDisplayName(assignments.role_display_name || assignments.role_name || "");
        setRoleDescription(assignments.role_description || "");
        setIsCurrentUser(
          Boolean(
            user?.b2c_object_id && assignments.b2c_object_id === user.b2c_object_id
          )
        );
        setRoles(rolesData || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load assignments.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, user?.b2c_object_id]);

  const handleToggleProperty = (propertyId) => {
    setAssignedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateUserAssignments(userId, assignedProperties, []);
      if (response.data && response.data.unit_ids) {
        setAssignedUnits(response.data.unit_ids);
      }
      const assignments = await getUserAssignments(userId);
      setAssignedProperties(assignments.property_ids || []);
      setAssignedUnits(assignments.unit_ids || []);
      showToast(
        "Property assignments updated. All units for assigned properties have been automatically assigned."
      );
    } catch (err) {
      console.error("Update failed:", err);
      showToast(err.response?.data?.error || "Failed to update assignments.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box className="page-container">
          <Skeleton height={40} width={250} />
          <Skeleton height={60} sx={{ mt: 2 }} />
          <Skeleton height={60} sx={{ mt: 1 }} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box className="page-container">
        <Typography variant="h4" fontWeight={600} sx={{ mb: 2 }}>
          Manage Access
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {roleDisplayName && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={1}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Current role
            </Typography>
            <Typography fontWeight={500}>{roleDisplayName}</Typography>
            {roleDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {roleDescription}
              </Typography>
            )}
          </Paper>
        )}

        {isGlobalRole && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {roleDisplayName
              ? `${roleDisplayName} is a global role and cannot be assigned to specific properties or units.`
              : "This user has a global role and cannot be assigned to specific properties or units."}
          </Alert>
        )}

        {isCurrentUser && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You cannot change your own assignments.
          </Alert>
        )}

        <RolesReferenceTable roles={roles} sx={{ mb: 3 }} />

        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={1}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Properties
          </Typography>

          {properties.length === 0 ? (
            <Typography>No properties available.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {properties.map((property) => (
                    <TableRow key={property.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={assignedProperties.includes(property.id)}
                          onChange={() => handleToggleProperty(property.id)}
                          disabled={isGlobalRole || isCurrentUser}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>{property.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {property.city || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || isGlobalRole || isCurrentUser}
            sx={{ minWidth: 150 }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>

          <Button variant="outlined" onClick={() => goBack()}>
            Back
          </Button>
        </Box>

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast((t) => ({ ...t, open: false }))}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}

export default UserAssignmentsPage;
